/**
 * ArticleFolder Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore.
 * Handles ArticleFolder entity for organizing article drafts into folders.
 *
 * Clone of the proven folder-converter family (onboardingFolderConverter,
 * abTestFolderConverter, webABTestFolderConverter).
 */

import { FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, Timestamp } from 'firebase/firestore';

/**
 * ArticleFolder interface
 * Represents a folder for organizing article drafts
 * Uses flat structure with parentFolderId for nested folders (optional future support)
 */
export interface ArticleFolder {
  id: string;
  projectId: string;
  name: string;
  parentFolderId: string | null; // null = root level folder
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
}

/**
 * Firestore converter for ArticleFolder
 * Handles Date<->Timestamp conversion and Client SDK signature compliance
 */
export const articleFolderConverter: FirestoreDataConverter<ArticleFolder> = {
  /**
   * Convert ArticleFolder to Firestore format
   * - Converts ISO date strings to Firestore Timestamps
   * - Removes id from data (stored in document metadata)
   */
  toFirestore(folder: ArticleFolder): Record<string, unknown> {
    const { id, ...data } = folder;

    return {
      ...data,
      createdAt: Timestamp.fromDate(new Date(data.createdAt)),
      updatedAt: Timestamp.fromDate(new Date(data.updatedAt)),
    };
  },

  /**
   * Convert Firestore document to ArticleFolder
   * - Converts Firestore Timestamps to ISO date strings
   * - Adds document id to returned object
   * - Uses Client SDK signature (snapshot, options?)
   */
  fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): ArticleFolder {
    const data = snapshot.data(options);

    return {
      id: snapshot.id,
      projectId: data.projectId,
      name: data.name,
      parentFolderId: data.parentFolderId ?? null,
      createdAt: data.createdAt?.toDate?.()
        ? data.createdAt.toDate().toISOString()
        : data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()
        ? data.updatedAt.toDate().toISOString()
        : data.updatedAt,
    };
  },
};
